// CPI-848: which contact the VN booking screen reads for each purchaser.
//
// When a booking reaches paid_verified, the backend clones every purchaser's live contact
// into a "snapshot" row and stores {customer_id, snapshot_contact_id, ...} refs on the
// opportunity (snapshot_contacts) plus a snapshot_frozen_at stamp. From then on the screen
// reads the snapshot copy, so later edits to the customer's own record no longer rewrite the
// details of an already-verified sale.
//
// Before the freeze — and for every booking created before this shipped — there are no
// refs, and the screen reads customer.contact_ids[0] exactly as it always did. That fallback
// is the whole backward-compatibility story; nothing else special-cases old bookings.
//
// Resolution is PER PURCHASER, never per booking. A freeze can legitimately produce a ref
// for one purchaser and none for another (a customer with no contact_ids is skipped), and
// that purchaser must still render from live data rather than blank.

export const getSnapshotContacts = (source) => (
  Array.isArray(source?.snapshot_contacts) ? source.snapshot_contacts : []
);

export const isSnapshotFrozen = (source) => Boolean(source?.snapshot_frozen_at);

// The ref for one purchaser, or null. A ref with no snapshot_contact_id counts as absent so
// the caller falls back to the live contact instead of querying an empty id.
export const snapshotContactFor = (source, customerId) => {
  if (!customerId) return null;
  return getSnapshotContacts(source)
    .find((ref) => ref && ref.customer_id === customerId && ref.snapshot_contact_id) || null;
};

// The contact id the screen should read for this purchaser: the snapshot when one exists,
// otherwise the live contact, otherwise ''.
export const purchaserContactId = (source, customer) => (
  snapshotContactFor(source, customer?.id)?.snapshot_contact_id
  || customer?.contact_ids?.[0]
  || ''
);

// Stable dependency keys for effects. The queries here run with fetchPolicy 'no-cache', which
// hands back a fresh array object on every fetch, so array identity is useless as a dep.
// Both keys are order-independent and ignore refs that could never resolve, so identical
// data always yields an identical key.
export const snapshotRefsKey = (source) => getSnapshotContacts(source)
  .filter((ref) => ref && ref.customer_id && ref.snapshot_contact_id)
  .map((ref) => `${ref.customer_id}:${ref.snapshot_contact_id}`)
  .sort()
  .join('|');

// One entry per purchaser with the contact id the screen would READ for them. Changes when
// a snapshot ref arrives AND when a live contact_ids[0] churns (pre-freeze versioning), so an
// effect keyed on it refetches in exactly those two cases and no others.
export const purchaserContactKey = (source, customers) => (
  Array.isArray(customers) ? customers : []
)
  .map((customer) => `${customer?.id || ''}:${purchaserContactId(source, customer)}`)
  .sort()
  .join('|');
